import { defineModule, } from 'direct-vuex';
import type { Feature, } from '~/api/ui-client';
import { DirectDebitDetail, } from '~/utils/models';

export type RecommendedAmountThreshold = {
	recommendedAmount: number | null,
	minimumAmount: number | null,
	maximumAmount: number | null,
};

export type ErrorResponse = 'API_ERROR' | null;

export type DirectDebitModuleState = {
	currentStep: 1 | 2 | 3 | null,
	isAccountNumberUpdated: boolean,
	existingDdDetails: DirectDebitDetail | null,
	paymentDateInApiDateFormat: string | null,
	recommendedAmountThreshold: RecommendedAmountThreshold,
	amendDdJourneyFeatures: Feature[],
	amendDdJourneyFeaturesError: ErrorResponse,
} & DirectDebitDetail;

export default defineModule({
	namespaced: true as const,
	state: {
		currentStep: 1,
		isAccountNumberUpdated: false,
		existingDdDetails: null,
		bankDetail: {
			accountHolderName: '',
			bankAccountNo: '',
			sortCode: '',
		},
		paymentFrequency: 'M',
		paymentDate: 1,
		ddPaymentAmount: null,
		paymentDateInApiDateFormat: null,
		recommendedAmountThreshold: {
			recommendedAmount: null,
			minimumAmount: null,
			maximumAmount: null,
		},
		amendDdJourneyFeatures: [] as Feature,
		amendDdJourneyFeaturesError: null as ErrorResponse,
	} as DirectDebitModuleState,
	getters: {
		hasExistingBankDetails(state) {
			return state.existingDdDetails !== null;
		},
		hasBankDetails(state) {
			return state.bankDetail?.accountHolderName !== '';
		},
	},
	mutations: {
		setStep(state, step: DirectDebitModuleState['currentStep']) {
			state.currentStep = step;
		},
		setExistingDetails(state, detail: DirectDebitDetail) {
			state.existingDdDetails = detail;
			state.bankDetail = detail.bankDetail;
			state.paymentDate = detail.paymentDate;
			state.paymentFrequency = detail.paymentFrequency;
			state.ddPaymentAmount = detail.ddPaymentAmount;
		},
		setDdDetail(state, detail: DirectDebitDetail) {
			state.paymentDate = detail.paymentDate;
			state.paymentFrequency = detail.paymentFrequency;
			state.ddPaymentAmount = detail.ddPaymentAmount;
		},
		setDDPaymentAmount(state, ddPaymentAmount) {
			state.ddPaymentAmount = ddPaymentAmount;
		},
		setPaymentDateIntoApiDateFormat(state, date:string) {
			state.paymentDateInApiDateFormat = date;
		},
		setBankDetail(state, detail: DirectDebitDetail) {
			state.bankDetail = detail.bankDetail;
		},
		setAccountNumberUpdated(state,arg:boolean) {
			state.isAccountNumberUpdated = arg;
		},
		setRecommendedAmountThreshold(state, details:RecommendedAmountThreshold) {
			state.recommendedAmountThreshold = { ...details, };
		},
		setAmendDdJorneyFeature(state, featuresList: Feature[]) {
			state.amendDdJourneyFeatures = featuresList;
		},
		setAmendDdJorneyFeatureError(state, errorResponse: ErrorResponse) {
			state.amendDdJourneyFeaturesError = errorResponse;
		},
		reset(state) {
			state.currentStep = null;
			state.existingDdDetails = null;
			state.paymentDate = 1;
			state.paymentFrequency = 'M';
			state.ddPaymentAmount = null;
			state.bankDetail = {
				accountHolderName: '',
				bankAccountNo: '',
				sortCode: '',
			};
			state.recommendedAmountThreshold = {
				recommendedAmount: null,
				minimumAmount: null,
				maximumAmount: null,
			};
			state.amendDdJourneyFeatures = [];
			state.amendDdJourneyFeaturesError = null;
		},
	},
});
